import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  useDisclosure,
  TableContainer,
  Textarea,
} from '@chakra-ui/react';
import { FiUpload, FiDownload, FiTrash2, FiEye, FiEdit } from 'react-icons/fi';
import { referenceDataApi } from '../services/referenceDataApi';

/**
 * 参照データ管理コンポーネント
 */
const ReferenceDataManager = ({ projectId }) => {
  const [referenceDataList, setReferenceDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [dataName, setDataName] = useState('');
  const [dataDescription, setDataDescription] = useState('');
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isViewOpen, 
    onOpen: onViewOpen, 
    onClose: onViewClose 
  } = useDisclosure();
  
  // 参照データ一覧を取得
  const fetchReferenceDataList = async () => {
    setIsLoading(true);
    try {
      const response = await referenceDataApi.getReferenceDataList(projectId);
      setReferenceDataList(response.data);
    } catch (error) {
      console.error('Failed to fetch reference data list:', error);
      
      // 404エラー（データが存在しない）の場合はエラー表示しない
      if (error.response?.status !== 404) {
        toast({
          title: '参照データの取得に失敗しました',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // 404の場合は空の配列を設定
      if (error.response?.status === 404) {
        setReferenceDataList([]);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // ファイル選択時の処理
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // ファイル形式チェック
      const allowedTypes = ['application/json', 'text/csv', 'application/vnd.ms-excel'];
      const allowedExtensions = ['.json', '.csv'];
      
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        toast({
          title: 'ファイル形式エラー',
          description: 'JSONまたはCSVファイルを選択してください。',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setSelectedFile(file);
      setDataName(file.name.replace(/\.[^/.]+$/, "")); // 拡張子を除いたファイル名をデフォルトに
      onOpen();
    }
  };
  
  // 参照データをアップロード
  const handleUpload = async () => {
    if (!selectedFile || !dataName.trim()) {
      toast({
        title: 'エラー',
        description: 'ファイルと名前を指定してください。',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', dataName);
    formData.append('description', dataDescription);
    
    try {
      await referenceDataApi.createReferenceData(projectId, formData);
      
      toast({
        title: '参照データを登録しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // リストを再取得
      await fetchReferenceDataList();
      
      // モーダルを閉じてフォームをリセット
      onClose();
      setSelectedFile(null);
      setDataName('');
      setDataDescription('');
      
    } catch (error) {
      console.error('Failed to upload reference data:', error);
      toast({
        title: '参照データの登録に失敗しました',
        description: error.response?.data?.detail || 'エラーが発生しました。',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  // 参照データを削除
  const handleDelete = async (referenceDataId) => {
    if (!window.confirm('この参照データを削除しますか？')) {
      return;
    }
    
    try {
      await referenceDataApi.deleteReferenceData(projectId, referenceDataId);
      
      toast({
        title: '参照データを削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // リストを再取得
      await fetchReferenceDataList();
      
    } catch (error) {
      console.error('Failed to delete reference data:', error);
      toast({
        title: '参照データの削除に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // 参照データの詳細を表示
  const handleView = async (referenceDataId) => {
    try {
      const response = await referenceDataApi.getReferenceData(projectId, referenceDataId);
      setSelectedData(response.data);
      onViewOpen();
    } catch (error) {
      console.error('Failed to fetch reference data details:', error);
      toast({
        title: '参照データの取得に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // ファイル形式の判定
  const getFileType = (fileName) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.') + 1);
    return extension;
  };
  
  // ファイルサイズのフォーマット
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // コンポーネントマウント時に参照データ一覧を取得
  useEffect(() => {
    fetchReferenceDataList();
  }, [projectId]);
  
  return (
    <Box>
      {/* アップロードセクション */}
      <Box
        bg={useColorModeValue('white', 'gray.800')}
        p={6}
        borderRadius="lg"
        boxShadow="sm"
        mb={6}
      >
        <VStack spacing={4} align="stretch">
          <Text fontSize="lg" fontWeight="bold">参照データをアップロード</Text>
          <Text color="gray.600">
            JSONまたはCSVファイルをアップロードして、OCR処理時にAIが参照できるデータとして利用できます。
            商品マスタや顧客リストなどを登録することで、より正確なOCR結果が期待できます。
          </Text>
          
          <Input
            type="file"
            accept=".json,.csv"
            onChange={handleFileSelect}
            display="none"
            id="reference-data-upload"
          />
          <Button
            as="label"
            htmlFor="reference-data-upload"
            leftIcon={<FiUpload />}
            colorScheme="brand"
            cursor="pointer"
          >
            ファイルを選択
          </Button>
        </VStack>
      </Box>
      
      {/* 参照データ一覧 */}
      <Box
        bg={useColorModeValue('white', 'gray.800')}
        p={6}
        borderRadius="lg"
        boxShadow="sm"
      >
        <Text fontSize="lg" fontWeight="bold" mb={4}>登録済み参照データ</Text>
        
        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner />
          </Flex>
        ) : referenceDataList.length === 0 ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            参照データが登録されていません
          </Alert>
        ) : (
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>名前</Th>
                  <Th>説明</Th>
                  <Th>ファイル形式</Th>
                  <Th>ファイルサイズ</Th>
                  <Th>登録日</Th>
                  <Th>アクション</Th>
                </Tr>
              </Thead>
              <Tbody>
                {referenceDataList.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <Text fontWeight="bold">{data.name}</Text>
                    </Td>
                    <Td>
                      <Text noOfLines={2} maxW="200px">
                        {data.description || '説明なし'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getFileType(data.original_filename) === 'json' ? 'blue' : 'green'}
                      >
                        {getFileType(data.original_filename).toUpperCase()}
                      </Badge>
                    </Td>
                    <Td>{formatFileSize(data.file_size)}</Td>
                    <Td>
                      <Text fontSize="sm">
                        {new Date(data.created_at).toLocaleDateString('ja-JP')}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          aria-label="詳細表示"
                          onClick={() => handleView(data.id)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          aria-label="削除"
                          onClick={() => handleDelete(data.id)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
      
      {/* アップロードモーダル */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>参照データを登録</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>名前 <Text as="span" color="red.500">*</Text></FormLabel>
                <Input
                  value={dataName}
                  onChange={(e) => setDataName(e.target.value)}
                  placeholder="例: 商品マスタ、顧客リスト"
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  OCR処理時のプルダウンでこの名前が表示されます
                </Text>
              </FormControl>
              
              <FormControl>
                <FormLabel>説明</FormLabel>
                <Textarea
                  value={dataDescription}
                  onChange={(e) => setDataDescription(e.target.value)}
                  placeholder="このデータの用途や内容について説明してください（任意）"
                  rows={3}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  参照データの目的や使用方法を記載しておくと便利です
                </Text>
              </FormControl>
              
              {selectedFile && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">{selectedFile.name}</Text>
                    <Text fontSize="sm">
                      {formatFileSize(selectedFile.size)}
                    </Text>
                  </Box>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              キャンセル
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleUpload}
              isLoading={isUploading}
              loadingText="登録中..."
            >
              登録
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* 詳細表示モーダル */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>参照データ詳細</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedData && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>基本情報</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <HStack>
                      <Text fontWeight="bold" width="100px">名前:</Text>
                      <Text>{selectedData.name}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" width="100px">説明:</Text>
                      <Text>{selectedData.description || '説明なし'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" width="100px">ファイル名:</Text>
                      <Text>{selectedData.original_filename}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" width="100px">ファイルサイズ:</Text>
                      <Text>{formatFileSize(selectedData.file_size)}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" width="100px">登録日:</Text>
                      <Text>{new Date(selectedData.created_at).toLocaleString('ja-JP')}</Text>
                    </HStack>
                  </VStack>
                </Box>
                
                <Box>
                  <Text fontWeight="bold" mb={2}>データプレビュー</Text>
                  <Box
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    p={4}
                    borderRadius="md"
                    maxH="300px"
                    overflowY="auto"
                  >
                    <Text
                      fontSize="sm"
                      fontFamily="monospace"
                      whiteSpace="pre-wrap"
                    >
                      {selectedData.content_preview || 'プレビューデータがありません'}
                    </Text>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>閉じる</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ReferenceDataManager;